import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import AuthContext from '../../context/authContext';
import { currencyFormatter, percentFormatter, decimalFormatter } from '../../util/formatHelper';
import useBranch from '../../hooks/useBranch';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_AREA_STAFF_COST } from '../../constants/statisticTypes';
import { isFeatureEnabled } from '../../config/featureFlags';
import StaffCostEstimateNotice from '../ui/StaffCostEstimateNotice';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getRechartsTooltipLabelStyle,
  getStatisticsChartCursorFill,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getRechartsLegendBarProps,
  getStatisticsHtmlThStyle,
} from '../../util/chartTheme';

const defaultStartDate = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
const DEFAULT_EMPLOYER_COST_FACTOR = '1.3';

// Known business areas for the selector (matches util/staff-cost-config.js).
// Areas actually present in the payload are merged in as well.
const KNOWN_AREAS = [
  'IT',
  'Marketing',
  'Callcenter',
  'B2B',
  'Arenaleitung',
  'Kundenbetreuung',
  'Buchhaltung',
  'HR',
  'Management',
  'Geschäftsführung',
  'Axe',
];

const formatCurrency = value => currencyFormatter.format(value ?? 0);
const formatHours = value => `${decimalFormatter.format(value ?? 0)} h`;
const formatPercent = value =>
  value === null || value === undefined ? '–' : `${percentFormatter.format(value)} %`;

// `presetArea` locks the view to a single business area (used when embedded,
// e.g. the IT statistic's "Personalkosten" tab) — the area selector is hidden.
const AreaStaffCostStatistics = ({ presetArea = null }) => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartCursorFill = useMemo(() => getStatisticsChartCursorFill(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const tooltipLabelStyle = useMemo(() => getRechartsTooltipLabelStyle(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const costColor = theme.palette.primary.main;

  const cardStyle = useMemo(
    () => ({
      background: statSurfaceBg,
      borderRadius: '8px',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    }),
    [statSurfaceBg]
  );

  const authCtx = useContext(AuthContext);
  const canView =
    authCtx.checkRight('statistic:special:staffCost');

  const [searchParams] = useSearchParams();
  const initialArea = presetArea || (searchParams.get('area') || 'all').trim() || 'all';

  const { getBranches } = useBranch();
  const getBranchesRef = useRef(getBranches);
  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);

  const [branchOptions, setBranchOptions] = useState([]);
  const [branchesReady, setBranchesReady] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedArea, setSelectedArea] = useState(initialArea);
  const employerCostFactorRef = useRef(DEFAULT_EMPLOYER_COST_FACTOR);

  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [chartType, setChartType] = useState('bar');
  const [selectedCacheEntryId, setSelectedCacheEntryId] = useState(null);

  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    branchId: '',
    area: initialArea,
    employerCostFactor: DEFAULT_EMPLOYER_COST_FACTOR,
  });

  useEffect(() => {
    let cancelled = false;
    const mapBranch = b => ({
      _id: typeof b._id === 'string' ? b._id : String(b._id),
      label: b.locationName || b.longName || 'Standort',
    });
    (async () => {
      if (!canView) return;
      try {
        const list = await getBranchesRef.current();
        if (cancelled) return;
        setBranchOptions([{ _id: '', label: 'Alle Standorte' }, ...(list || []).map(mapBranch)]);
      } catch (_) {
        if (!cancelled) setBranchOptions([{ _id: '', label: 'Alle Standorte' }]);
      }
      if (!cancelled) setBranchesReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const applyFilters = (nextStart, nextEnd) => {
    const s = nextStart || startDate;
    const e = nextEnd || endDate;
    if (!s || !e || !s.isValid() || !e.isValid()) {
      toast.error('Bitte wähle gültige Daten aus');
      return;
    }
    if (s.isAfter(e)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }
    const rawFactorInput = employerCostFactorRef.current ? employerCostFactorRef.current.value : '';
    const rawFactor = Number(String(rawFactorInput).replace(',', '.'));
    const employerCostFactor =
      Number.isFinite(rawFactor) && rawFactor > 0 ? String(rawFactor) : DEFAULT_EMPLOYER_COST_FACTOR;

    setSelectedCacheEntryId(null);
    setAppliedFilter({
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      branchId: selectedBranchId || '',
      area: selectedArea || 'all',
      employerCostFactor,
    });
  };

  const shouldQuery = canView && branchesReady;

  const filterParams = useMemo(() => {
    if (!shouldQuery) return null;
    return {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branches: appliedFilter.branchId || '',
      area: appliedFilter.area || 'all',
      employerCostFactor: appliedFilter.employerCostFactor,
    };
  }, [appliedFilter, shouldQuery]);

  const job = useStatisticJob({
    statisticType: STATISTIC_TYPE_AREA_STAFF_COST,
    endpoint: '/api/v1/statistic/areaStaffCost',
    filterParams,
  });
  const stats = job.payload;

  useEffect(() => {
    if (job.status === 'failed' && job.error) {
      toast.error(`Fehler beim Laden der Personalkosten-Statistik: ${job.error}`);
    }
  }, [job.status, job.error]);

  const { entries: cacheEntries, isLoading: cacheEntriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_AREA_STAFF_COST,
  });

  const handleSelectCacheEntry = useCallback(
    entryId => {
      setSelectedCacheEntryId(entryId);
      if (entryId) job.loadCacheEntry(entryId);
      else job.fetch();
    },
    [job]
  );

  useEffect(() => {
    setStartDate(dayjs(appliedFilter.startDate));
    setEndDate(dayjs(appliedFilter.endDate));
  }, [appliedFilter]);

  const isAllAreas = (appliedFilter.area || 'all').toLowerCase() === 'all';

  const areaOptions = useMemo(() => {
    const set = new Set(KNOWN_AREAS);
    (stats?.areas || []).forEach(a => a?.area && set.add(a.area));
    return Array.from(set);
  }, [stats]);

  const areaChartData = useMemo(
    () => (stats?.areas || []).map(a => ({ ...a, label: a.area })),
    [stats]
  );
  const dailyChartData = useMemo(
    () =>
      (stats?.timeSeries?.daily || []).map(p => ({
        ...p,
        label: dayjs(p.date).format('DD.MM.'),
      })),
    [stats]
  );

  const kpiCards = useMemo(() => {
    if (!stats?.totals) return [];
    const t = stats.totals;
    const cards = [
      {
        label: 'Personalkosten gesamt',
        value: formatCurrency(t.staffCost),
        helper: `inkl. Arbeitgeber-Faktor ×${stats?.meta?.employerCostFactor ?? ''}`,
      },
      { label: 'Arbeitsstunden', value: formatHours(t.hours), helper: 'Produktive Stunden' },
      { label: 'Mitarbeiter (aktiv)', value: t.headcount ?? 0, helper: 'Personen mit Arbeitszeit im Zeitraum' },
      {
        label: 'Ø Kosten / Stunde',
        value: t.hours > 0 ? formatCurrency(t.staffCost / t.hours) : '–',
        helper: 'Durchschnittliche Personalkosten je Stunde',
      },
      {
        label: 'Überstunden',
        value: formatHours(t.overtimeHours),
        helper: `> ${stats?.meta?.weeklyNormalHours ?? 40} h/Woche je Mitarbeiter`,
      },
      {
        label: 'Abgegoltene Überstunden',
        value: formatHours(t.compensatedOvertimeHours),
        helper: 'Gehalts-Rollen – keine Zusatzkosten',
      },
      {
        label: 'Vergütete Überstunden',
        value: formatHours(t.paidOvertimeHours),
        helper: 'Stundenlohn-Rollen – kostenwirksam',
      },
    ];
    if (isAllAreas) {
      cards.push({
        label: 'Bereiche',
        value: (stats.areas || []).length,
        helper: 'Anzahl Bereiche mit Kosten',
      });
    }
    return cards;
  }, [stats, isAllAreas]);

  if (!canView) return <Navigate to='/welcome' replace />;
  // The standalone "Gesamt / Bereiche" overview is hidden behind a feature flag
  // for now (see FEATURE_FLAGS.md). The embedded, area-locked usage (e.g. the IT
  // statistic's "Personalkosten" tab via presetArea) is unaffected.
  if (!presetArea && !isFeatureEnabled('staffCostAreaOverview')) {
    return <Navigate to='/welcome' replace />;
  }

  const areaFilter = (
    <FormControl fullWidth>
      <InputLabel id='area-staff-cost-area-label'>Bereich</InputLabel>
      <Select
        labelId='area-staff-cost-area-label'
        label='Bereich'
        value={selectedArea}
        onChange={event => setSelectedArea(event.target.value)}
      >
        <MenuItem value='all'>Alle Bereiche</MenuItem>
        {areaOptions.map(a => (
          <MenuItem key={a} value={a}>
            {a}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const employerCostFactorFilter = (
    <TextField
      fullWidth
      type='number'
      label='Arbeitgeber-Faktor'
      defaultValue={DEFAULT_EMPLOYER_COST_FACTOR}
      inputRef={employerCostFactorRef}
      inputProps={{ step: '0.05', min: '1', max: '5' }}
    />
  );

  const renderKpiCards = () => {
    if (!stats) return null;
    return (
      <section>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {kpiCards.map(card => (
            <Box
              key={card.label}
              sx={{ ...cardStyle, flex: '1 1 220px', minWidth: 0, maxWidth: '100%' }}
            >
              <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
              <strong style={{ fontSize: '1.5rem', overflowWrap: 'anywhere' }}>{card.value}</strong>
              <small style={{ color: kpiHelperColor }}>{card.helper}</small>
            </Box>
          ))}
        </Box>
      </section>
    );
  };

  const renderAreaChart = () => {
    if (!stats || !isAllAreas || !areaChartData.length) return null;
    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Personalkosten nach Bereich
        </Typography>
        <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 360, minWidth: 0 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={areaChartData} layout='vertical' margin={{ left: 24 }}>
              <CartesianGrid stroke={chartGridStroke} horizontal={false} />
              <XAxis
                type='number'
                stroke={axisStroke}
                tickFormatter={value => currencyFormatter.format(value)}
              />
              <YAxis type='category' dataKey='label' stroke={axisStroke} width={120} />
              <Tooltip
                cursor={{ fill: chartCursorFill }}
                contentStyle={chartTooltipContentStyle}
                labelStyle={tooltipLabelStyle}
                formatter={value => formatCurrency(value)}
              />
              <Bar dataKey='staffCost' name='Personalkosten' fill={costColor} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </section>
    );
  };

  const renderDailyChart = () => {
    if (!stats || !dailyChartData.length) return null;
    return (
      <section>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
          sx={{ mb: 1 }}
        >
          <Typography variant='h6' component='h2' sx={{ m: 0 }}>
            Personalkosten im Zeitverlauf
          </Typography>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            size='small'
            onChange={(_, value) => value && setChartType(value)}
          >
            <ToggleButton value='bar'>Balken</ToggleButton>
            <ToggleButton value='line'>Linie</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 340, minWidth: 0 }}>
          <ResponsiveContainer width='100%' height='100%'>
            {chartType === 'bar' ? (
              <BarChart data={dailyChartData}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey='label' stroke={axisStroke} />
                <YAxis stroke={axisStroke} tickFormatter={value => currencyFormatter.format(value)} />
                <Tooltip
                  cursor={{ fill: chartCursorFill }}
                  contentStyle={chartTooltipContentStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={value => formatCurrency(value)}
                />
                <Legend {...chartLegendBarProps} />
                <Bar dataKey='staffCost' name='Personalkosten' fill={costColor} />
              </BarChart>
            ) : (
              <LineChart data={dailyChartData}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey='label' stroke={axisStroke} />
                <YAxis stroke={axisStroke} tickFormatter={value => currencyFormatter.format(value)} />
                <Tooltip
                  contentStyle={chartTooltipContentStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={value => formatCurrency(value)}
                />
                <Legend {...chartLegendBarProps} />
                <Line
                  type='monotone'
                  dataKey='staffCost'
                  name='Personalkosten'
                  stroke={costColor}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </Paper>
      </section>
    );
  };

  const renderAreaTable = () => {
    if (!stats?.areas?.length || !isAllAreas) return null;
    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Bereiche im Detail
        </Typography>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
            <thead>
              <tr>
                <th style={htmlThStyle()}>Bereich</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Personalkosten</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Anteil</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Stunden</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Überstunden</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Mitarbeiter</th>
              </tr>
            </thead>
            <tbody>
              {stats.areas.map(a => (
                <tr key={a.area}>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>{a.area}</td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatCurrency(a.staffCost)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatPercent(a.share)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatHours(a.hours)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatHours(a.overtimeHours)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {a.headcount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderBranchTable = () => {
    if (!stats?.byBranch?.length || stats.byBranch.length < 2) return null;
    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Nach Standort
        </Typography>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
            <thead>
              <tr>
                <th style={htmlThStyle()}>Standort</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Personalkosten</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Stunden</th>
              </tr>
            </thead>
            <tbody>
              {stats.byBranch.map(b => (
                <tr key={b.branchId || b.branchName}>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>{b.branchName}</td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatCurrency(b.staffCost)}
                  </td>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop, textAlign: 'right' }}>
                    {formatHours(b.hours)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderMethodNote = () => {
    if (!stats?.meta) return null;
    const { employerCostFactor, officeDefaultWage, shiftsWithUser, shiftsTotal, weeklyNormalHours } =
      stats.meta;
    return (
      <Paper
        elevation={0}
        sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', background: statSurfaceBg }}
      >
        <Typography variant='body2' color='text.secondary'>
          Bereichs-Zuordnung über die Rolle des Mitarbeiters (Fallback: Timebutler-Abteilung). Jede
          gearbeitete Stunde fließt ein – nicht zuordenbare Zeiten unter „Sonstige".
          Personalkosten = Stunden × Stundenlohn × Arbeitgeber-Faktor (×{employerCostFactor}).
          Stundenlohn: individueller Wert am Mitarbeiter, sonst Rollen-Default (Head of Marketing/IT/B2B
          gesondert), sonst {formatCurrency(officeDefaultWage)}/h. Überstunden = Stunden über{' '}
          {weeklyNormalHours ?? 40} h/Woche je Mitarbeiter; bei Gehalts-Rollen gelten sie als abgegolten
          (keine Zusatzkosten), bei Stundenlohn-Rollen werden sie vergütet. {shiftsWithUser} von{' '}
          {shiftsTotal} Schichten konnten einem Mitarbeiter zugeordnet werden.
        </Typography>
      </Paper>
    );
  };

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      branchOptions={branchOptions}
      selectedBranchId={selectedBranchId}
      onBranchChange={setSelectedBranchId}
      branchSelectIdPrefix='area-staff-cost-branch'
      extraFilters={presetArea ? [employerCostFactorFilter] : [areaFilter, employerCostFactorFilter]}
      onApply={applyFilters}
      status={job.status}
      isStale={job.isStale}
      cacheComputedAt={job.cacheComputedAt}
      cacheExpiresAt={job.cacheExpiresAt}
      job={job.job}
      error={job.error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedCacheEntryId}
      onSelectEntry={handleSelectCacheEntry}
      entriesLoading={cacheEntriesLoading}
      onRefresh={job.refresh}
      externallyLoading={!branchesReady}
      hasPayload={!!stats}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'clip',
          '& > *': { minWidth: 0, maxWidth: '100%', mb: 3 },
          '& > *:last-child': { mb: 0 },
        }}
      >
        <StaffCostEstimateNotice assumptions={stats?.meta?.assumptions} sx={{ mb: 1 }} />
        {renderKpiCards()}
        {renderAreaChart()}
        {renderDailyChart()}
        {renderAreaTable()}
        {renderBranchTable()}
        {renderMethodNote()}
      </Box>
    </StatisticViewShell>
  );
};

export default AreaStaffCostStatistics;
